// 회원 탭 페이지 — 운영진 전용 회원 마스터.
//
// 상태별 필터(활성/휴회/제외) + 이름 검색을 동시에 적용. JSX로 옮긴 뒤
// 검색 input은 useState로 관리되므로 필터 변경 시 input focus가 자연스럽게
// 유지된다 (legacy의 rebuildMemberList 트릭이 더 이상 필요 없음).
//
// 회원 등록/편집/일괄등록 시트와 FAB은 호출자(legacy.ts)가 콜백으로 주입해
// 시트 모듈 추출 전까지의 가교 역할.

import { useMemo, useState } from "preact/hooks";
import { api } from "../api";
import { canSeeNtrp, isAdmin, memberRoleLabel } from "../auth";
import { state } from "../state";
import { formatNtrp, genderLabel } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { matchName } from "../ui/text";

export interface RenderMembersParams {
  view: HTMLElement;
  onEditMember: (m: any) => void;
  onNewMember: (members: any[]) => void;
  onBulkRegister: (members: any[]) => void;
  renderFab: (onClick: () => void, label?: string) => void;
}

const STATUS_FILTERS = [
  { key: "active", label: "활성" },
  { key: "on_leave", label: "휴회" },
  { key: "excluded", label: "제외" },
] as const;

function statusOf(m: any): string {
  return m.member_status || "active";
}

function roleRank(m: any): number {
  if (m.app_role === "president") return 0;
  if (m.app_role === "staff" || m.app_role === "admin") return 1;
  return 2;
}

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

interface MembersListViewProps {
  members: any[];
  onEditMember: (m: any) => void;
  onBulkRegister: (members: any[]) => void;
}

function MembersListView({ members, onEditMember, onBulkRegister }: MembersListViewProps) {
  const initialFilter = state.memberStatusFilter || "active";
  if (!state.memberStatusFilter) state.memberStatusFilter = initialFilter;
  const [statusFilter, setStatusFilter] = useState<string>(initialFilter);
  const [query, setQuery] = useState<string>("");

  const counts = useMemo(() => {
    return members.reduce<Record<string, number>>((acc, m) => {
      const s = statusOf(m);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [members]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members
      .filter((m) => statusOf(m) === statusFilter)
      .filter((m) => matchName(m.name, q))
      .slice()
      .sort((a, b) => roleRank(a) - roleRank(b));
  }, [members, statusFilter, query]);

  const pinPendingCount = members.filter((m) => !m.pin_set && statusOf(m) === "active").length;
  const showAdminControls = isAdmin();
  const showNtrpHint = state.club?.ntrp_visibility !== "all_members";

  return (
    <div class="section">
      <div class="filter-chips">
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.key;
          return (
            <button
              type="button"
              key={f.key}
              class={"filter-chip" + (active ? " filter-chip--active" : "")}
              onClick={() => {
                state.memberStatusFilter = f.key;
                setStatusFilter(f.key);
              }}
            >
              {f.label} {counts[f.key] || 0}
            </button>
          );
        })}
      </div>

      <input
        type="text"
        class="input"
        placeholder="이름으로 검색…"
        value={query}
        onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
      />

      {showAdminControls && (
        <button
          type="button"
          class="btn btn--secondary btn--block"
          style="margin-top:8px"
          onClick={() => onBulkRegister(members)}
        >
          <LinkIcon />
          {pinPendingCount > 0
            ? `명단 일괄 등록 + 공용 초대 (${pinPendingCount}명 미가입)`
            : "명단 일괄 등록 + 공용 초대 링크"}
        </button>
      )}

      {showNtrpHint && (
        <p class="muted small" style="margin-top:4px;display:flex;align-items:center;gap:6px">
          <span class="badge badge--admin-only">운영진만 보여요</span>
          NTRP는 일반 회원에게 표시되지 않아요
        </p>
      )}

      <div class="section__title">
        <h3>회원</h3>
        <span class="count">{filtered.length}명</span>
      </div>

      {filtered.length === 0 ? (
        <p class="muted small">
          {query.trim() ? `"${query.trim()}" 검색 결과가 없어요` : "해당 상태의 회원이 없어요."}
        </p>
      ) : (
        <div class="list list--compact">
          {filtered.map((m) => {
            const roleLbl = memberRoleLabel(m.app_role);
            const roleClass = m.app_role === "president" ? " role-badge--president" : "";
            const showPending = !m.pin_set && statusOf(m) === "active";
            const levelText = m.level_category ? ` · ${m.level_category}` : "";
            const ntrpText = canSeeNtrp() && m.ntrp ? ` · NTRP ${formatNtrp(m.ntrp)}` : "";
            return (
              <button
                type="button"
                key={m.member_id}
                class="list__item"
                onClick={() => onEditMember(m)}
              >
                <div class="list__avatar">{(m.name || "?").slice(0, 1)}</div>
                <div class="list__main">
                  <p class="list__title">
                    {m.name}
                    {roleLbl && <span class={`role-badge${roleClass}`}>{roleLbl}</span>}
                    {showPending && (
                      <span
                        class="role-badge"
                        style="background:#fef3c7;color:#92400e;border-color:#fde68a"
                      >
                        미가입
                      </span>
                    )}
                  </p>
                  <p class="list__meta">
                    {m.gender_pending ? (
                      <span style="color:#c2410c;font-weight:600">성별 미설정 (탭하여 편집)</span>
                    ) : (
                      genderLabel(m.gender)
                    )}
                    {levelText}
                    {ntrpText}
                  </p>
                </div>
                <div class="list__trail">
                  <ChevronIcon />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export async function renderMembersList(params: RenderMembersParams): Promise<void> {
  const { view, onEditMember, onNewMember, onBulkRegister, renderFab } = params;
  view.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "불러오는 중…";
  view.append(loading);

  let members: any[];
  try {
    members = await api("/api/v1/members");
  } catch (e: any) {
    view.innerHTML = "";
    const err = document.createElement("div");
    err.className = "error-card";
    err.textContent = e.status === 403 ? "관리자 모드에서만 이용할 수 있어요" : e.message;
    view.append(err);
    return;
  }

  view.innerHTML = "";

  if (members.length === 0) {
    view.append(mountPreact(
      <div class="card empty">
        <div class="empty__big">👥</div>
        <div>아직 등록된 회원이 없어요</div>
      </div>,
    ));
  } else {
    view.append(mountPreact(
      <MembersListView
        members={members}
        onEditMember={onEditMember}
        onBulkRegister={onBulkRegister}
      />,
    ));
  }

  renderFab(() => onNewMember(members), "＋ 새 회원 추가");
}
